import { v4 as uuid } from 'uuid';

import { AccountGroup, AccountGroupId, AccountGroupRequest, CompleteAccountProperty } from '../generated-types';
import { GroupedAccountCardItem, GroupedItems, ProjectedAccountGroup } from '../models/account';
import { putAccountProperties } from './accountService';
import { CONTEXT_PATH } from './index';
import { get, put, post, del } from './request';

export const ACCOUNTS_GROUPS_SERVICE_PATH = 'rest/resource/accounts/groups';

export async function getAccountsGroups(): Promise<AccountGroup[]> {
    return get<AccountGroup[]>(`${CONTEXT_PATH}/${ACCOUNTS_GROUPS_SERVICE_PATH}`);
}

export async function addAccountGroup(tmpId: string, body: AccountGroupRequest): Promise<[string, string]> {
    const response = await post<AccountGroupId>(`${CONTEXT_PATH}/${ACCOUNTS_GROUPS_SERVICE_PATH}`, body);
    return [tmpId, response.value];
}

export async function updateAccountGroup({ id, body }: { id: string; body: AccountGroupRequest }): Promise<void> {
    return put<void>(`${CONTEXT_PATH}/${ACCOUNTS_GROUPS_SERVICE_PATH}/${id}`, body);
}

export async function deleteAccountGroup(id: string): Promise<null> {
    return del<null>(`${CONTEXT_PATH}/${ACCOUNTS_GROUPS_SERVICE_PATH}/${id}`);
}

export type GroupsUpdateArgs = {
    lastSaved: GroupedItems<GroupedAccountCardItem>;
    groupedAccounts: GroupedItems<GroupedAccountCardItem>;
};

export const mapToUpdateProperties = (items: GroupedItems<GroupedAccountCardItem>): CompleteAccountProperty[] => {
    return items.reduce((acc, group, groupIndex) => {
        const response = [
            ...acc,
            ...group.values.map((item, index) => ({
                accountNumber: item.accountNumber,
                sortingKey: index,
                frontpage: item.favorite ?? false,
                accountGroup: { id: { value: group.id }, name: group.name, sortingKey: groupIndex },
            })),
        ];

        return response;
    }, [] as CompleteAccountProperty[]);
};

export const getUpdatedGroups = ({ lastSaved, groupedAccounts }: GroupsUpdateArgs) => {
    return groupedAccounts
        .map((group, index) => [group, index] as [GroupedItems<GroupedAccountCardItem>[number], number])
        .filter(([group]) => group.values.length !== 0)
        .filter(([group]) => !group.isTemp)
        .filter(([group]) => lastSaved.find((g) => g.id === group.id)?.name !== group.name);
};

export const updateGroups = async (groupsUpdateArgs: GroupsUpdateArgs) => {
    const updatedGroupsUpdateRequests = getUpdatedGroups(groupsUpdateArgs).map(([group, index]) =>
        updateAccountGroup({ id: group.id, body: { name: group.name, sortingKey: index } }),
    );

    await Promise.all(updatedGroupsUpdateRequests);
};

export const getNewGroups = (groupedAccounts: GroupedItems<GroupedAccountCardItem>) => {
    return groupedAccounts
        .map((group, index) => [group, index] as [(typeof groupedAccounts)[number], number])
        .filter(([group]) => group.values.length !== 0)
        .filter(([group]) => group.isTemp);
};

export const getDeletedGroups = (groupedAccounts: GroupedItems<GroupedAccountCardItem>) => {
    return groupedAccounts.filter((group) => group.values.length === 0).filter((group) => !group.isTemp);
};

export const updateGroupsAndProperties = async (groupsUpdateArgs: GroupsUpdateArgs) => {
    await updateGroups(groupsUpdateArgs);

    const { groupedAccounts } = groupsUpdateArgs;

    const newGroups = getNewGroups(groupedAccounts);

    const newGroupsResponse = await Promise.all(
        newGroups.map(([group, index]) => addAccountGroup(group.id, { name: group.name, sortingKey: index })),
    );

    newGroupsResponse.forEach(([oldId, id]) => {
        const group = groupedAccounts.find((group) => group.id === oldId);
        if (group) {
            group.id = id;
        }
    });

    const modifiedAccountProperties = mapToUpdateProperties(groupedAccounts);
    await putAccountProperties(modifiedAccountProperties);

    const unusedGroupsDeleteRequests = getDeletedGroups(groupedAccounts).map((group) => deleteAccountGroup(group.id));

    await Promise.all(unusedGroupsDeleteRequests);
};

export const simulateMissingGroups = (groups: AccountGroup[], groupName: string): ProjectedAccountGroup[] =>
    groups.length === 0 ? [{ id: { value: uuid() as string }, name: groupName, isTemp: true }] : groups;
